import { logPush } from './push-diagnostics';
import { triggerFirebaseTokenFetch, checkWindowToken } from './webviewgold-bridge';
import { getCachedToken, setCachedToken } from './token-cache';
import { isIOSWebViewGold } from './webviewgold-detection';

export type PrefetchState = 'idle' | 'fetching' | 'success' | 'failed';

const RETRY_STORAGE_KEY = 'webviewgold_retry_count';
const MAX_RETRIES = 3;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 15000;
const BACKOFF_DELAYS = [1000, 2000, 4000];

let currentState: PrefetchState = 'idle';
let fetchedToken: string | null = null;
let pollingInterval: number | null = null;
let timeoutHandle: number | null = null;
let prefetchStartTime: number | null = null;

export function getPrefetchState(): PrefetchState {
  return currentState;
}

export function getTokenFromCache(): string | null {
  return fetchedToken;
}

export function getTimeSinceStart(): number | null {
  if (!prefetchStartTime) return null;
  return Date.now() - prefetchStartTime;
}

function getRetryCount(): number {
  try {
    const count = sessionStorage.getItem(RETRY_STORAGE_KEY);
    return count ? parseInt(count, 10) : 0;
  } catch {
    return 0;
  }
}

function setRetryCount(count: number): void {
  try {
    sessionStorage.setItem(RETRY_STORAGE_KEY, count.toString());
  } catch (error) {
    logPush('warn', 'retry', 'Failed to save retry count', error);
  }
}

function clearPolling(): void {
  if (pollingInterval !== null) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  if (timeoutHandle !== null) {
    clearTimeout(timeoutHandle);
    timeoutHandle = null;
  }
}

async function pollForToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const startTime = Date.now();
    let pollCount = 0;

    logPush('info', 'prefetch', 'Starting token polling');

    pollingInterval = window.setInterval(() => {
      pollCount++;
      const token = checkWindowToken();

      if (token) {
        logPush('success', 'prefetch', `Token found after ${pollCount} polls (${Date.now() - startTime}ms)`);
        clearPolling();
        resolve(token);
        return;
      }

      const elapsed = Date.now() - startTime;
      if (elapsed >= POLL_TIMEOUT_MS) {
        logPush('warn', 'prefetch', `Polling timeout after ${pollCount} polls (${elapsed}ms)`);
        clearPolling();
        resolve(null);
      }
    }, POLL_INTERVAL_MS);

    timeoutHandle = window.setTimeout(() => {
      logPush('error', 'prefetch', `Polling timeout reached (${POLL_TIMEOUT_MS}ms)`);
      clearPolling();
      resolve(null);
    }, POLL_TIMEOUT_MS);
  });
}

async function attemptFetch(): Promise<string | null> {
  logPush('info', 'prefetch', 'Attempting token fetch');

  try {
    await triggerFirebaseTokenFetch();

    const token = await pollForToken();

    if (token) {
      setCachedToken(token);
      return token;
    }

    return null;
  } catch (error) {
    logPush('error', 'prefetch', 'Fetch attempt failed', error);
    return null;
  }
}

export async function startTokenPrefetch(): Promise<string | null> {
  if (!isIOSWebViewGold()) {
    logPush('info', 'prefetch', 'Not iOS WebViewGold, skipping prefetch');
    return null;
  }

  if (currentState === 'fetching') {
    logPush('warn', 'prefetch', 'Prefetch already in progress');
    return null;
  }

  prefetchStartTime = Date.now();
  currentState = 'fetching';

  const cached = getCachedToken();
  if (cached.token) {
    logPush('success', 'prefetch', `Using cached token (source: ${cached.source})`);
    fetchedToken = cached.token;
    currentState = 'success';
    return cached.token;
  }

  logPush('info', 'prefetch', 'Starting fresh token prefetch');

  const token = await attemptFetch();

  if (token) {
    fetchedToken = token;
    currentState = 'success';
    setRetryCount(0);
    logPush('success', 'prefetch', `Prefetch successful in ${getTimeSinceStart()}ms`);
    return token;
  }

  currentState = 'failed';
  logPush('error', 'prefetch', 'Prefetch failed after initial attempt');
  return null;
}

export async function retryTokenFetch(): Promise<string | null> {
  if (!isIOSWebViewGold()) {
    logPush('warn', 'retry', 'Cannot retry: not iOS WebViewGold');
    return null;
  }

  const retryCount = getRetryCount();

  if (retryCount >= MAX_RETRIES) {
    logPush('error', 'retry', `Maximum retries reached (${MAX_RETRIES})`);
    return null;
  }

  const backoffDelay = BACKOFF_DELAYS[retryCount] || BACKOFF_DELAYS[BACKOFF_DELAYS.length - 1];

  logPush('info', 'retry', `Retry attempt ${retryCount + 1}/${MAX_RETRIES} after ${backoffDelay}ms delay`);

  await new Promise(resolve => setTimeout(resolve, backoffDelay));

  currentState = 'fetching';
  prefetchStartTime = Date.now();

  const token = await attemptFetch();

  if (token) {
    fetchedToken = token;
    currentState = 'success';
    setRetryCount(0);
    logPush('success', 'retry', `Retry successful on attempt ${retryCount + 1}`);
    return token;
  }

  setRetryCount(retryCount + 1);
  currentState = 'failed';
  logPush('error', 'retry', `Retry ${retryCount + 1} failed`);
  return null;
}

export function resetPrefetch(): void {
  clearPolling();
  currentState = 'idle';
  fetchedToken = null;
  prefetchStartTime = null;
  setRetryCount(0);
  logPush('info', 'prefetch', 'Prefetch state reset');
}

export async function waitForToken(maxWaitMs: number = 15000): Promise<string | null> {
  if (fetchedToken) {
    return fetchedToken;
  }

  if (currentState === 'success') {
    return fetchedToken;
  }

  if (currentState === 'idle') {
    return startTokenPrefetch();
  }

  if (currentState === 'fetching') {
    const startWait = Date.now();
    logPush('info', 'prefetch', `Waiting for ongoing prefetch (max ${maxWaitMs}ms)`);

    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (fetchedToken || currentState === 'success') {
          clearInterval(checkInterval);
          logPush('success', 'prefetch', 'Token became available during wait');
          resolve(fetchedToken);
          return;
        }

        if (currentState === 'failed' || Date.now() - startWait >= maxWaitMs) {
          clearInterval(checkInterval);
          logPush('warn', 'prefetch', 'Wait for token timed out or failed');
          resolve(null);
        }
      }, 500);
    });
  }

  return null;
}
